import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { lazy, Suspense, useReducer, useEffect, useRef, useState, useCallback } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useBootstrap } from '@/auth/bootstrap-context';
import SyncPausedBanner from '@/modules/sync/sync-paused-banner';
import BillingReturnPage from '@/pages/BillingReturn';
import BudgetPage from '@/pages/Budget';
import DashboardPage from '@/pages/Dashboard';
import HomePage from '@/pages/Home';
import ProfilePage from '@/pages/Profile';
const StatementImportPage = lazy(() => import('@/pages/StatementImport'));
import { loadBudgets, saveBudgets, loadProfile, saveProfile } from '@/storage/storage';
import { loadUIState, saveUIState } from '@/storage/ui-state';
import { AppContext } from '@/store/context';
import { isCloudSyncPaused, isBlockedWhilePaused } from '@/store/read-only-guard';
import { reducer, initState, defaultProfile } from '@/store/reducer';
import { ALL_TUTORIAL_KEYS } from '@/common/components/tutorial-spotlight/keys';
import { updateProfile } from '@/modules/profile/profile-api';
import { writeOutboxForChanges } from '@/sync/diff-writer';
import { OUTBOX_CHANGED_EVENT } from '@/sync/sync-triggers';
import { SyncProvider } from '@/sync/sync-context';
import { uid } from '@/utils';
function App() {
    const { offline, data } = useBootstrap();
    const cloudSyncStatus = data?.cloudSyncStatus ?? null;
    const paused = isCloudSyncPaused(cloudSyncStatus);
    const [state, dispatch] = useReducer(reducer, initState, (init) => {
        const ui = loadUIState();
        return { ...init, ...ui };
    });
    const [ready, setReady] = useState(false);
    const initRef = useRef(false);
    const prevBudgetsRef = useRef([]);
    // Reload budgets from IDB into React state (called after sync pull applies remote changes)
    const reloadFromIdb = useCallback(async () => {
        const budgets = await loadBudgets();
        prevBudgetsRef.current = budgets; // update ref so diff-writer doesn't re-process pulled data
        dispatch({ type: 'INIT_DATA', budgets, profile: state.profile });
    }, [state.profile]);
    // IDB migrations are already done in AppShell before App mounts.
    // Here we only load data from IDB.
    useEffect(() => {
        if (initRef.current)
            return;
        initRef.current = true;
        (async () => {
            const [budgets, rawProfile] = await Promise.all([loadBudgets(), loadProfile()]);
            const base = rawProfile ? { ...rawProfile, id: rawProfile.id || uid() } : defaultProfile;
            // Профиль server-authoritative: сливаем серверные значения (bootstrap) с локальными
            // синхронно ДО первого рендера страниц (иначе подсказки мигнут на новом устройстве).
            // Пустые серверные поля не затирают локальные. seenTutorials — объединение сервера,
            // локального профиля и легаси localStorage-ключей (миграция старого состояния).
            const sp = data?.profile;
            const legacySeen = ALL_TUTORIAL_KEYS.filter((k) => localStorage.getItem(k));
            const seenTutorials = Array.from(new Set([...(sp?.seenTutorials ?? []), ...(base.seenTutorials ?? []), ...legacySeen]));
            const profile = {
                ...base,
                name: sp?.displayName || base.name,
                avatar: sp?.avatar || base.avatar,
                seenTutorials,
            };
            prevBudgetsRef.current = budgets; // initialize prev so diff-writer doesn't treat everything as new
            dispatch({ type: 'INIT_DATA', budgets, profile });
            setReady(true);
            // Локальные/легаси подсказки, которых нет на сервере, мигрируем на сервер.
            if (sp && seenTutorials.some((k) => !(sp.seenTutorials ?? []).includes(k))) {
                updateProfile({ seenTutorials }).catch(() => { });
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // Persist UI state to localStorage (sync)
    useEffect(() => {
        if (!ready)
            return;
        saveUIState({
            year: state.year,
            month: state.month,
            selectedDate: state.selectedDate,
            calMode: state.calMode,
            view: state.view,
            budgetView: state.budgetView,
            currentId: state.currentId,
        });
    }, [
        ready,
        state.year,
        state.month,
        state.selectedDate,
        state.calMode,
        state.view,
        state.budgetView,
        state.currentId,
    ]);
    // Persist budgets to IndexedDB (async) + write outbox for CloudBound changes
    useEffect(() => {
        if (!ready)
            return;
        saveBudgets(state.budgets);
        const prev = prevBudgetsRef.current;
        if (prev.length > 0 || state.budgets.length > 0) {
            writeOutboxForChanges(prev, state.budgets).then((added) => {
                // Есть новые несинхронизированные изменения → обновить бэдж и разбудить
                // авто-синк. Только при added > 0, иначе после pull словим цикл синка.
                if (added > 0)
                    window.dispatchEvent(new Event(OUTBOX_CHANGED_EVENT));
            });
        }
        prevBudgetsRef.current = state.budgets;
    }, [ready, state.budgets]);
    // Persist profile to IndexedDB (async)
    useEffect(() => {
        if (!ready)
            return;
        saveProfile(state.profile);
    }, [ready, state.profile]);
    // Stage 3 read-only: while cloud sync is paused (expired/revoked/past-due subscription),
    // block mutations to CloudBound budgets. The outbox is left intact for later reactivation.
    const guardedDispatch = useCallback((action) => {
        if (paused && isBlockedWhilePaused(action, state.budgets, state.currentId))
            return;
        dispatch(action);
    }, [paused, state.budgets, state.currentId]);
    if (!ready)
        return null;
    return (_jsx(AppContext.Provider, { value: { state, dispatch: guardedDispatch }, children: _jsxs(SyncProvider, { enabled: !offline && !paused, onDataChanged: reloadFromIdb, children: [paused && _jsx(SyncPausedBanner, { reasonCode: cloudSyncStatus?.reasonCode ?? null }), offline && _jsx("div", { className: "offline-badge", children: "\u041E\u0444\u0444\u043B\u0430\u0439\u043D" }), _jsxs(Routes, { children: [_jsx(Route, { path: "/", element: _jsx(Navigate, { to: "/app", replace: true }) }), _jsx(Route, { path: "/app", element: _jsx(HomePage, {}) }), _jsx(Route, { path: "/app/profile", element: _jsx(ProfilePage, {}) }), _jsx(Route, { path: "/billing/return", element: _jsx(BillingReturnPage, {}) }), _jsx(Route, { path: "/bdgts/:budgetId/cal", element: _jsx(BudgetPage, {}) }), _jsx(Route, { path: "/bdgts/:budgetId/cat", element: _jsx(DashboardPage, {}) }), _jsx(Route, { path: "/bdgts/:budgetId/import", element: _jsx(Suspense, { fallback: null, children: _jsx(StatementImportPage, {}) }) }), _jsx(Route, { path: "*", element: _jsx(Navigate, { to: "/app", replace: true }) })] })] }) }));
}
export default App;
